import type { SnackbarKey } from 'notistack';
import { useSnackbar } from 'notistack';
import type { ReactNode } from 'react';

import { getErrorMessage } from '@/lib/apiError';

interface NotifyOptions {
  /** Si es true, el snackbar no se cierra solo — requiere cierre manual. */
  persist?: boolean;
  /** Botones/acciones a mostrar en el snackbar (ej: "Ver detalle"). */
  action?: (key: SnackbarKey) => ReactNode;
}

/**
 * Hook for showing toast notifications.
 *
 * Usage:
 *   const notify = useNotify();
 *   notify.success('Profesional creado');
 *   notify.fromError(err); // auto-extracts message from ApiError
 */
export function useNotify() {
  const { enqueueSnackbar } = useSnackbar();

  return {
    success(message: string): void {
      enqueueSnackbar(message, { variant: 'success' });
    },
    error(message: string, options?: NotifyOptions): void {
      enqueueSnackbar(message, { variant: 'error', ...options });
    },
    info(message: string): void {
      enqueueSnackbar(message, { variant: 'info' });
    },
    warning(message: string, options?: NotifyOptions): void {
      enqueueSnackbar(message, { variant: 'warning', ...options });
    },
    fromError(err: unknown, fallback?: string): void {
      const message = getErrorMessage(err) || fallback || 'Ocurrió un error.';
      enqueueSnackbar(message, { variant: 'error' });
    },
  };
}
